import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { useI18n } from "../../i18n/I18nProvider";
import { extractError } from "../../lib/errors";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { EmptyState } from "../../components/EmptyState";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../../components/ui/sheet";
import { Plus, Search } from "lucide-react";

const FINDING_TYPES = [
  { value: "conformity", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  { value: "nonconformity", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  { value: "opportunity", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  { value: "observation", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
];

/**
 * AuditFindingsTab — ISO 19011 §6.4.7.
 * Append-only: shows existing findings + Sheet to record new finding.
 * nonconformity type auto-creates NC record on backend.
 */
const AuditFindingsTab = ({ audit }) => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    finding_type: "conformity",
    control_number: "",
    framework: audit.frameworks?.[0] || "",
    severity: "",
    description: "",
    // NC-specific (only when finding_type=nonconformity)
    nc_type: "minor",
    root_cause: "",
    corrective_action: "",
    assigned_to: "",
    due_date: "",
  });

  const fetchFindings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audits/${audit.audit_id}/findings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFindings(res.data);
    } catch (e) {
      console.error("Failed to fetch findings", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFindings();
  }, [audit.audit_id]);

  const recordFinding = async () => {
    if (!form.description.trim()) {
      toast.error(t("audits.toast.desc_required"));
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/audits/${audit.audit_id}/findings`,
        {
          control_number: form.control_number || undefined,
          framework: form.framework || undefined,
          finding_type: form.finding_type,
          severity: form.finding_type === "nonconformity" ? (form.severity || "minor") : undefined,
          description: form.description,
          nc_type: form.finding_type === "nonconformity" ? form.nc_type : undefined,
          root_cause: form.finding_type === "nonconformity" ? form.root_cause : undefined,
          corrective_action: form.finding_type === "nonconformity" ? form.corrective_action : undefined,
          assigned_to: form.finding_type === "nonconformity" ? form.assigned_to : undefined,
          due_date: form.finding_type === "nonconformity" ? form.due_date : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("audits.toast.finding_created"));
      setShowAdd(false);
      setForm({
        finding_type: "conformity",
        control_number: "",
        framework: audit.frameworks?.[0] || "",
        severity: "",
        description: "",
        nc_type: "minor",
        root_cause: "",
        corrective_action: "",
        assigned_to: "",
        due_date: "",
      });
      fetchFindings();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.finding_failed")));
    } finally {
      setSaving(false);
    }
  };

  const getTypeBadgeColor = (ftype) =>
    FINDING_TYPES.find((f) => f.value === ftype)?.color || "";

  return (
    <div className="flex flex-col gap-4">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {findings.length} {t("audits.tab.findings")}
        </span>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t("audits.findings.add")}
        </Button>
      </div>

      {/* Findings list */}
      {loading ? (
        <div className="py-8 text-center text-muted-foreground text-sm">{t("loading")}</div>
      ) : findings.length === 0 ? (
        <EmptyState
          icon={Search}
          title={t("audits.findings.none")}
          description={t("audits.findings.none_desc")}
          action={
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t("audits.findings.add")}
            </Button>
          }
        />
      ) : (
        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-2 pr-4 pb-4">
            {findings.map((f) => (
              <Card key={f.finding_id} className="border-border">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className={`text-xs ${getTypeBadgeColor(f.finding_type)}`}>
                          {t(`audits.findings.type.${f.finding_type}`)}
                        </Badge>
                        {f.severity && (
                          <Badge variant="secondary" className="text-xs capitalize">
                            {t(`audits.findings.severity_${f.severity}`)}
                          </Badge>
                        )}
                        {f.control_number && (
                          <span className="font-mono text-xs text-muted-foreground">{f.control_number}</span>
                        )}
                        {f.nc_id && (
                          <span className="text-xs text-muted-foreground">
                            {t("audits.findings.nc_linked")}: {f.nc_id}
                          </span>
                        )}
                      </div>
                      <p className="text-sm">{f.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(f.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Add finding Sheet */}
      <Sheet open={showAdd} onOpenChange={setShowAdd}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("audits.findings.log_title")}</SheetTitle>
            <SheetDescription>{t("audits.findings.log_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label>{t("audits.findings.type_label")}</Label>
              <Select
                value={form.finding_type}
                onValueChange={(v) => setForm({ ...form, finding_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FINDING_TYPES.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>
                      {t(`audits.findings.type.${ft.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.finding_type === "nonconformity" && (
              <div className="space-y-2">
                <Label>{t("audits.findings.severity")}</Label>
                <Select
                  value={form.severity || "minor"}
                  onValueChange={(v) => setForm({ ...form, severity: v, nc_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">{t("audits.findings.severity_minor")}</SelectItem>
                    <SelectItem value="major">{t("audits.findings.severity_major")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("audits.findings.control")} ({t("optional")})</Label>
              <Input
                value={form.control_number}
                onChange={(e) => setForm({ ...form, control_number: e.target.value })}
                placeholder="A.5.1"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("audits.findings.description")} *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t("audits.findings.desc_ph")}
                rows={3}
              />
            </div>

            {form.finding_type === "nonconformity" && (
              <>
                <div className="space-y-2">
                  <Label>{t("audits.nc.root_cause")}</Label>
                  <Textarea
                    value={form.root_cause}
                    onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                    placeholder={t("audits.nc.cause_ph")}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("audits.nc.action")}</Label>
                  <Textarea
                    value={form.corrective_action}
                    onChange={(e) => setForm({ ...form, corrective_action: e.target.value })}
                    placeholder={t("audits.nc.action_ph")}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("audits.nc.assigned")}</Label>
                    <Input
                      value={form.assigned_to}
                      onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("audits.nc.due")}</Label>
                    <Input
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowAdd(false)}>{t("cancel")}</Button>
            <Button onClick={recordFinding} disabled={saving}>
              {saving ? "..." : t("audits.findings.add")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AuditFindingsTab;
